import { useCallback, useEffect, useState } from 'react';
import UseCreateRelatedEntitiesWSVParams from './useCreateRelatedEntitiesWSV.props';
import { AllocationScenarioDetailSecurityModel, CapTableDetailsModel } from '../../../../../../api';
import { CapTableVersion } from '../../../../../../common/types/company';
import { ValuationByAllocation } from '../../../../../../common/types/valuation';
import { getCapTableVersionsByCompanyAndMD } from '../../../../../../services/hooks/companies/useGetCapTableVersionsByCompanyAndMD/useGetCapTableVersionsByCompanyAndMD';
import { getValuationByAllocation } from '../../../../../../services/hooks/valuations/useGetValuationByAllocation/useGetValuationByAllocation';
import { getNumberValue, Logger } from '../../../../../../utilities';
import { createWeightedShareValuesConfiguration } from '../../../../allocation/WeightedShareValues/config';
import { RelatedEntity, RelatedEntityMeasurementDate } from '../../../../types';
import { Configuration } from '../../types';

const useCreateRelatedEntityWSV = ({
  childCompanies,
  fieldAttributes,
  funds,
  isUniformCurrency,
  currentMeasurementDate,
  parentCompanyCapTable,
  holdingAllocations,
}: UseCreateRelatedEntitiesWSVParams) => {
  const [configurations, setConfigurations] = useState<Configuration[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Get the child company's measurement date relative to the parent company's measurement date
   * @param childCompany - The child company to get the measurement date for
   * @returns The child company's measurement date
   */
  const getChildMDRelativeToParentMD = useCallback(
    (childCompany: RelatedEntity): RelatedEntityMeasurementDate => {
      const currentMd = childCompany.company_measurement_dates.find(
        (md: RelatedEntityMeasurementDate) => md.date === currentMeasurementDate.date
      );

      if (!currentMd) throw new Error('No matching measurement date found for child company');
      return currentMd;
    },
    [currentMeasurementDate]
  );

  const getValuation = useCallback(
    async (childCompany: RelatedEntity): Promise<ValuationByAllocation> => {
      const currentAllocation = holdingAllocations.find(
        alloc => alloc.company_id === getNumberValue(childCompany.company_id)
      );

      if (!currentAllocation) {
        throw new Error(`No allocation found for child company ${childCompany.company_name}`);
      }

      const valuation = await getValuationByAllocation({
        allocationId: currentAllocation.id,
      });

      if (!valuation) {
        throw new Error(
          `No valuation found for child company ${childCompany.company_name} for allocation ${currentAllocation.id}`
        );
      }

      return valuation;
    },
    [holdingAllocations]
  );

  const getPrimaryCapTable = useCallback(
    async (childCompany: RelatedEntity): Promise<CapTableVersion> => {
      const currentMd = getChildMDRelativeToParentMD(childCompany);

      const capTableVersions = await getCapTableVersionsByCompanyAndMD({
        companyId: +childCompany.company_id,
        companyMeasurementDateId: currentMd.id,
      });
      if (!capTableVersions)
        throw new Error(
          `No cap table versions found for child company ${childCompany.company_name} on ${currentMd.date}`
        );

      const primaryCapTable = capTableVersions.find(v => v.is_primary);
      if (!primaryCapTable)
        throw new Error(
          `No primary cap table found for child company ${childCompany.company_name} on ${currentMd.date}`
        );

      return primaryCapTable;
    },
    [getChildMDRelativeToParentMD]
  );

  const generateWSVConfiguration = useCallback(
    async (childCompany: RelatedEntity) => {
      const [primaryCapTable, valuation] = await Promise.allSettled([
        getPrimaryCapTable(childCompany),
        getValuation(childCompany),
      ]);

      if (primaryCapTable.status === 'rejected' || valuation.status === 'rejected') {
        const errorMessage = [
          primaryCapTable.status === 'rejected' ? primaryCapTable.reason : '',
          valuation.status === 'rejected' ? valuation.reason : '',
        ].join('\n');

        Logger.error(errorMessage, {
          childCompany,
          primaryCapTable,
          valuation,
        });
        throw new Error(errorMessage);
      }

      return createWeightedShareValuesConfiguration({
        // for Related Entities, the WSV calculations must use the childCompany cap table but the parentCompany fund ownership
        capTable: {
          ...primaryCapTable.value,
          fund_ownership: parentCompanyCapTable.fund_ownership,
        } as CapTableDetailsModel,
        securities: primaryCapTable.value.securities as AllocationScenarioDetailSecurityModel[],
        fieldAttributes,
        companyInfo: {
          name: childCompany.company_name,
          id: +childCompany.company_id,
        },
        funds,
        isUniformCurrency,
        valuation: valuation.value,
        isChildCompany: true,
      });
    },
    [getPrimaryCapTable, getValuation, parentCompanyCapTable, fieldAttributes, funds, isUniformCurrency]
  );

  const handleWSVListChange = useCallback(async () => {
    try {
      setIsLoading(true);

      // parallelize all child comp config
      const childCompProms = childCompanies.map(childCompany => generateWSVConfiguration(childCompany));
      const childCompWSVs = await Promise.allSettled(childCompProms);

      const childCompWSVsResolved = childCompWSVs
        .filter(wsv => wsv.status === 'fulfilled')
        .map(wsv => wsv.value)
        .flat();

      // Would be ideal here to push an error alert indicating X companies will not have WSVs
      const childCompWSVsRejected = childCompWSVs.filter(wsv => wsv.status === 'rejected').map(wsv => wsv.reason);
      if (childCompWSVsRejected.length > 0) {
        Logger.error('Error generating WSV for child companies ', {
          childCompWSVsRejected,
        });
      }

      setConfigurations([...childCompWSVsResolved]);
    } catch (error) {
      Logger.error('Error generating WSV for child companies ', error);
    } finally {
      setIsLoading(false);
    }
  }, [childCompanies, generateWSVConfiguration]);

  useEffect(() => {
    handleWSVListChange();
  }, [childCompanies, handleWSVListChange]);

  return { configurations, isLoading };
};

export default useCreateRelatedEntityWSV;
